/**
 * Resources Hub Page
 * Free tools, quizzes, and templates for relationship health
 * Optimized for SEO and lead generation
 */

import { useNavigate } from 'react-router';
import { useSEO, useStructuredData, COMPETITIVE_KEYWORD_CLUSTERS, mergeKeywordSets } from '../lib/seo';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Heart,
  Calculator,
  FileText,
  Download,
  CheckCircle2,
  ArrowRight,
  Users,
  Brain,
  MessageCircle,
  BookOpen,
  Target,
  Sparkles,
} from 'lucide-react';
import { StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { MarketingSiteHeader } from '../components/marketing/MarketingSiteHeader';

interface Resource {
  id: string;
  title: string;
  description: string;
  icon: any;
  category: 'tool' | 'quiz' | 'template' | 'guide';
  downloadable: boolean;
  featured: boolean;
}

const resources: Resource[] = [
  {
    id: 'tension-calculator',
    title: 'Relationship Tension Calculator',
    description: 'Get an instant tension score (RTI) from 5 behavior signals. Free assessment takes 3 minutes.',
    icon: Calculator,
    category: 'tool',
    downloadable: false,
    featured: true,
  },
  {
    id: 'attachment-quiz',
    title: 'Attachment Style Quiz',
    description: 'Discover your attachment pattern (anxious, avoidant, secure, or disorganized) in relationships.',
    icon: Users,
    category: 'quiz',
    downloadable: false,
    featured: true,
  },
  {
    id: 'communication-templates',
    title: 'Difficult Conversation Templates',
    description: 'Pre-written scripts for addressing tension, expressing needs, and resolving conflicts.',
    icon: MessageCircle,
    category: 'template',
    downloadable: true,
    featured: true,
  },
  {
    id: 'anxiety-workbook',
    title: 'Relationship Anxiety Workbook',
    description: 'Comprehensive PDF workbook with exercises to manage relationship anxiety and build security.',
    icon: BookOpen,
    category: 'guide',
    downloadable: true,
    featured: false,
  },
  {
    id: 'check-in-questions',
    title: '50 Daily Check-In Questions',
    description: 'Thought-provoking questions to deepen emotional intimacy and stay connected.',
    icon: FileText,
    category: 'template',
    downloadable: true,
    featured: false,
  },
  {
    id: 'conflict-resolution-guide',
    title: 'Conflict Resolution Framework',
    description: 'Step-by-step guide to resolving disagreements without damaging your relationship.',
    icon: Target,
    category: 'guide',
    downloadable: true,
    featured: false,
  },
];

export default function Resources() {
  const navigate = useNavigate();

  // SEO Configuration
  useSEO({
    title: 'Free Relationship Resources - Quizzes, Tools & Templates | TENSION',
    description: 'Free relationship tools including a tension score calculator, attachment style quiz, communication templates, and anxiety workbooks. Science-backed resources for healthier relationships.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.conflict as unknown as string[],
      ['relationship tools', 'attachment style quiz', 'free relationship resources', 'relationship counseling app'],
    ),
    canonical: '/resources',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  // Structured Data
  useStructuredData({
    type: 'Organization',
    data: {
      name: 'TENSION Resources',
      description: 'Free relationship tools and resources',
      url: 'https://tensionlabs.net/resources',
    },
  });

  const featuredResources = resources.filter(r => r.featured);
  const allResources = resources.filter(r => !r.featured);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      tool: 'from-blue-500 to-cyan-500',
      quiz: 'from-purple-500 to-pink-500',
      template: 'from-green-500 to-emerald-500',
      guide: 'from-orange-500 to-red-500',
    };
    return colors[category] || 'from-neutral-500 to-neutral-600';
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen">
      <MarketingSiteHeader variant="hub" activeNav="resources" />

      {/* Hero Section */}
      <section className="relative px-4 pb-4 pt-8 md:pt-12">
        <div className="marketing-panel mx-auto grid max-w-6xl gap-8 md:grid-cols-[1.05fr_0.95fr] md:items-stretch">
          <div className="flex flex-col justify-center p-8 text-center md:p-12 md:text-left">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-violet-100/90 px-4 py-2 md:mx-0">
              <Sparkles className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-semibold text-violet-800">100% free tools</span>
            </div>
            <h1 className="mt-5 text-4xl font-black tracking-tight text-neutral-900 md:text-5xl">
              Free relationship tools & resources
            </h1>
            <p className="mt-4 text-lg text-neutral-600 md:text-xl">
              Science-backed quizzes, calculators, templates, and guides to build healthier relationships. No signup
              required.
            </p>
          </div>
          <div className="relative min-h-[220px]">
            <StockImage
              stockKey="supportCalm"
              width={900}
              height={700}
              priority
              alt="Modern support context for relationship growth and communication"
              className="h-full min-h-[220px] w-full object-cover md:rounded-none md:rounded-r-3xl"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/90 via-transparent to-transparent md:from-white/70"
              aria-hidden
            />
          </div>
        </div>
      </section>

      <section className="px-4 pb-8">
        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          <Card className="border-violet-100 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-neutral-900">Best couples app list</h2>
            <p className="mt-2 text-sm text-neutral-600">Compare top options and pick the right fit for your goals.</p>
            <Button variant="outline" className="mt-3 w-full" onClick={() => navigate('/best-couples-app')}>
              Open list
            </Button>
          </Card>
          <Card className="border-violet-100 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-neutral-900">TENSION vs Paired</h2>
            <p className="mt-2 text-sm text-neutral-600">See a factual side-by-side on workflows and fit.</p>
            <Button variant="outline" className="mt-3 w-full" onClick={() => navigate('/compare/tension-vs-paired')}>
              View comparison
            </Button>
          </Card>
          <Card className="border-violet-100 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-neutral-900">Paired alternatives</h2>
            <p className="mt-2 text-sm text-neutral-600">Explore alternatives focused on communication quality.</p>
            <Button variant="outline" className="mt-3 w-full" onClick={() => navigate('/alternatives/paired')}>
              Explore
            </Button>
          </Card>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-zinc-50 mb-2 text-center">Most Popular</h2>
          <p className="text-zinc-300 text-center mb-10">Start with these essential tools</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {featuredResources.map(resource => {
              const Icon = resource.icon;
              const gradientColor = getCategoryColor(resource.category);
              
              return (
                <Card
                  key={resource.id}
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group border-2 border-transparent hover:border-purple-200"
                >
                  <div className={`h-48 bg-gradient-to-br ${gradientColor} flex items-center justify-center relative`}>
                    <Icon className="w-24 h-24 text-white/90" />
                    {resource.downloadable && (
                      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full">
                        <div className="flex items-center gap-1 text-white text-xs font-semibold">
                          <Download className="w-3 h-3" />
                          Downloadable
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full mb-3 capitalize">
                      {resource.category}
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-900 mb-3 group-hover:text-purple-600 transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-neutral-600 mb-6">
                      {resource.description}
                    </p>
                    <Button
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      onClick={() => {/* Handle resource access */}}
                    >
                      {resource.downloadable ? 'Download Now' : 'Start Now'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* All Resources */}
      <section className="bg-white/65 px-4 py-16 backdrop-blur dark:bg-[#090d1a99]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-neutral-900 mb-10 text-center">All Resources</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {allResources.map(resource => {
              const Icon = resource.icon;
              const gradientColor = getCategoryColor(resource.category);
              
              return (
                <Card
                  key={resource.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  <div className={`h-32 bg-gradient-to-br ${gradientColor} flex items-center justify-center`}>
                    <Icon className="w-16 h-16 text-white/90" />
                  </div>
                  <div className="p-5">
                    <div className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full mb-2 capitalize">
                      {resource.category}
                    </div>
                    <h3 className="text-lg font-bold text-neutral-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-neutral-600 text-sm mb-4">
                      {resource.description}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {/* Handle resource access */}}
                    >
                      {resource.downloadable ? (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </>
                      ) : (
                        <>
                          Access Now
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-zinc-50 mb-12 text-center">
            Why Use TENSION Resources?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-zinc-50 mb-3">Science-Backed</h3>
              <p className="text-zinc-300">
                Created by relationship psychologists using evidence-based research and attachment theory.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-zinc-50 mb-3">100% Free</h3>
              <p className="text-zinc-300">
                No hidden costs, no credit card required. All resources are completely free forever.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-zinc-50 mb-3">Actionable</h3>
              <p className="text-zinc-300">
                Practical tools you can use immediately to improve your relationship health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-5xl rounded-3xl border border-cyan-300/20 bg-gradient-to-r from-neutral-950 via-purple-950 to-neutral-950 p-10 text-center text-white shadow-2xl shadow-purple-900/30">
          <h2 className="text-4xl font-bold mb-6">
            Want Personalized Insights?
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Join TENSION to track your relationship tension over time with daily check-ins, score forecasting, and personalized action cards.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/early-access')}
            className="bg-white text-purple-600 hover:bg-neutral-100 font-semibold px-8 py-6 text-lg"
          >
            Join Beta Now
            <ArrowRight className="w-6 h-6 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-violet-500/20 bg-[#090d1ae0] py-12 px-4 text-neutral-400">
        <div className="max-w-7xl mx-auto grid md:grid-cols-5 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">TENSION</span>
            </div>
            <p className="text-sm">
              Science-backed relationship tension monitor
            </p>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-3">Product</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/pricing')} className="block hover:text-white">Pricing</button>
              <button onClick={() => navigate('/about')} className="block hover:text-white">About</button>
              <button onClick={() => navigate('/faq')} className="block hover:text-white">FAQ</button>
              <button onClick={() => navigate('/home#relationship-intelligence')} className="block hover:text-white">Relationship Intelligence</button>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-3">Content</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/blog')} className="block hover:text-white">Blog</button>
              <button onClick={() => navigate('/resources')} className="block hover:text-white">Free Tools</button>
              <button onClick={() => navigate('/guides')} className="block hover:text-white">Guides</button>
              <button onClick={() => navigate('/press')} className="block hover:text-white">Press</button>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-3">Features</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/features/relationship-intelligence')} className="block hover:text-white">Intelligence Suite Page</button>
              <button onClick={() => navigate('/features/conversation-micro-coach')} className="block hover:text-white">Micro-Coach Page</button>
              <button onClick={() => navigate('/relationship-intelligence/faq')} className="block hover:text-white">Intelligence FAQ</button>
              <button onClick={() => navigate('/site-map')} className="block hover:text-white">Site Map</button>
              <button onClick={() => navigate('/support')} className="block hover:text-white">Support</button>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-3">Business & Legal</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => navigate('/business-inquiries')} className="block hover:text-white">Investors & Business Inquiries</button>
              <button onClick={() => navigate('/contact')} className="block hover:text-white">Contact</button>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-neutral-800 text-center text-sm">
          © 2026 TENSION Labs. All rights reserved.
        </div>
      </footer>
    </div>
    </WarmMarketingBackdrop>
  );
}
